import React from 'react';
import useAuth from '@/hooks/useAuth';
import { batchModal, batchPaymentsPage } from '@/utils/translations';
import { ScheduledBankPaymentGroupStateEnum } from '@/features/payments/types/BatchPayment';
import { TPaymentListItem } from '../types/ManagePayments';
import {
  STATUS_LABEL,
  STATUS_STYLE,
  isBatchAllFailed,
  isScheduledWithoutMultiSign
} from '../data/paymentStatus';
import { useApprovalDisplayState } from '../hooks/useApprovalDisplayState';

interface PaymentStatusPillProps {
  payment: TPaymentListItem;
}

/**
 * The list/table row equivalent of the status label already used inside ScheduledPaymentModal/
 * BatchModal (see useApprovalDisplayState, shared by all three so they can't drift out of
 * alignment) - a PENDING_APPROVAL row otherwise always renders "Approval required" via
 * STATUS_LABEL regardless of viewer, which is misleading for an admin with no authority to act
 * on this particular multi-sign action. Only a row's own multiSignActionId is enough to answer
 * that, so this fetches it per-row rather than waiting for a row to be opened in a modal - the
 * fetch is cached per multiSignActionId for the session, so paginating/re-rendering the table
 * never re-fetches it.
 */
const PaymentStatusPill: React.FC<PaymentStatusPillProps> = ({ payment }) => {
  const auth = useAuth();
  const { hasNoApprovalAuthority, isAwaitingAdditionalApproval, isLoadingApprovalDecision } =
    useApprovalDisplayState(
      payment,
      payment.state === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL,
      auth?.customer?.id
    );

  if (isLoadingApprovalDecision) {
    return (
      <span className={STATUS_STYLE[payment.state]}>
        {batchPaymentsPage.noApprovalAuthorityStatus}
      </span>
    );
  }

  if (payment.forceCompleted) {
    return <span className="text-error-darkred">{batchModal.markedAsCompleteLabel}</span>;
  }

  if (hasNoApprovalAuthority) {
    return (
      <span className={STATUS_STYLE[payment.state]}>
        {batchPaymentsPage.noApprovalAuthorityStatus}
      </span>
    );
  }

  if (isAwaitingAdditionalApproval) {
    return (
      <span className={STATUS_STYLE[payment.state]}>
        {batchPaymentsPage.awaitingAdditionalApprovalStatusLabel}
      </span>
    );
  }

  // An APPROVED row with no multiSignActionId never actually went through anyone's approval - it
  // was simply scheduled. Reuses STATUS_STYLE[APPROVED]'s colour (still correct here) and only
  // swaps the label text - see isScheduledWithoutMultiSign.
  if (isScheduledWithoutMultiSign(payment.state, payment.multiSignActionId)) {
    return (
      <span className={STATUS_STYLE[payment.state]}>{batchPaymentsPage.status.scheduled}</span>
    );
  }

  // A COMPLETED_WITH_FAILURES row where every payment actually failed reads as "Failed" instead
  // of "Completed with failures" - the backend's own state can't be trusted to reflect that (see
  // isBatchAllFailed). A genuine FAILED row already renders correctly via STATUS_LABEL below.
  if (isBatchAllFailed(payment.state, payment.completedPayments)) {
    return <span className={STATUS_STYLE[payment.state]}>{batchPaymentsPage.status.failed}</span>;
  }

  return <span className={STATUS_STYLE[payment.state]}>{STATUS_LABEL[payment.state]}</span>;
};

export default PaymentStatusPill;
